import axios from "axios";
import {
  getAccessToken,
  getRefreshToken,
  clearTokens,
  redirectToLogin,
} from "../../lib/utils/tokenUtils";
import { base_url } from "../../base_urls";

let APIKit = axios.create({
  baseURL: base_url,
});

export const clearAuthData = () => {
  clearTokens();
  localStorage.removeItem("Otp");
};

const isAuthEndpoint = (url = "") =>
  url.includes("/webLogin") ||
  url.includes("/login") ||
  url.includes("/users/refresh") ||
  url.includes("/auth/");

/**
 * When access token expires, backend returns 401 or 403.
 * If we have a refreshToken, silently refresh and retry — no FE expiry checks.
 */
const shouldAttemptTokenRefresh = (error, originalRequest) => {
  if (!originalRequest || originalRequest._retry) return false;
  if (isAuthEndpoint(originalRequest.url)) return false;

  const status = error.response?.status;
  if (status !== 401 && status !== 403) return false;

  const code = error.response?.data?.code || error.response?.data?.error;
  if (code === "IP_NOT_ALLOWED") return false;

  return !!getRefreshToken();
};

APIKit.interceptors.request.use(
  (config) => {
    const token = getAccessToken();
    if (token) {
      config.headers["Authorization"] = token;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

APIKit.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    const status = error.response?.status;
    const errorCode = error.response?.data?.code;

    if (status === 403 && errorCode === "IP_NOT_ALLOWED") {
      const errorMessage = error.response?.data?.message;
      const userIp = error.response?.data?.yourIp;
      const ipError = new Error(
        errorMessage ||
          "Access denied. Your IP address is not authorized to access admin routes.",
      );
      ipError.code = "IP_NOT_ALLOWED";
      ipError.userIp = userIp;
      ipError.isIpWhitelistError = true;
      ipError.status = 403;
      return Promise.reject(ipError);
    }

    if (shouldAttemptTokenRefresh(error, originalRequest)) {
      originalRequest._retry = true;

      try {
        // Dynamic import avoids circular dependency (loginApi → APIKit)
        const { refreshAccessToken } = await import("../../apis/loginApi");
        await refreshAccessToken();

        const newAccessToken = getAccessToken();
        if (!newAccessToken) {
          throw new Error("Refresh succeeded but no access token stored");
        }

        originalRequest.headers.Authorization = newAccessToken;
        return APIKit(originalRequest);
      } catch (refreshError) {
        if (!getAccessToken()) {
          clearTokens();
          redirectToLogin();
        }
        return Promise.reject(refreshError);
      }
    }

    // No refresh token — cannot recover session
    if ((status === 401 || status === 403) && !getRefreshToken()) {
      clearTokens();
      redirectToLogin();
    }

    return Promise.reject(error);
  },
);

export default APIKit;
